export type ApplicationFormSlug =
  | 'account-creation'
  | 'customer-feedback'
  | 'internal-memo'
  | 'customise-items-transfer'
  | 'certificate-request'
  | 'pricing-update';


export type ApplicationSubmissionStatus =
  | 'pending_review'
  | 'approved'
  | 'rejected'
  | 'kiv'
  | 'saved'
  | 'posted'
  | 'cancelled'
  | 'new'
  | 'processing'
  | 'pending_approval';

export type CffRequestType = 'return' | 'exchange' | 'replenish' | '';

export type CffCreditNotes = 'required' | 'not_required' | '';

export type CffWarehouseAction = 'replenish' | 'exchange' | 'others' | '';

export type CustomerFeedbackItemRow = {
  item_code: string;
  quantity: string;
  uom: string;
};

/** Warehouse/Logistics CFF — matches Excel "Application Form" sheet fields. */
export type CustomerFeedbackFormData = {
  date: string;
  request_type: CffRequestType;
  company_code: string;
  company_name: string;
  address: string;
  postal_code: string;
  territory_code: string;
  contact_person: string;
  contact_number: string;
  /** Optional warehouse roles — not required. */
  picker: string;
  packer: string;
  driver: string;
  items: CustomerFeedbackItemRow[];
  /** One reason applies to all item lines (exported to Excel/PDF Reason rows). */
  reason: string;
  /** Portal-only notes — never written to Excel/PDF export. */
  internal_remarks: string;
  invoice_no: string;
  invoice_date: string;
  credit_notes: CffCreditNotes;
  submitted_by: string;
  /** Who issued the sales invoice (shown as Submitted by (Invoice Issued by) in the list). */
  invoice_issued_by: string;
  /** Kept for PDF layout; not edited in the web form. */
  warehouse_action: CffWarehouseAction;
  warehouse_action_others: string;
  warehouse_reason: string;
  verified_by: string;
  approved_by: string;
  ack_date: string;
  posted_at?: string;
  posted_by?: string;
  cancelled_at?: string;
  cancelled_by?: string;
  /** Signed copy uploaded on Post (like internal memorandum). */
  signature_copy_name?: string;
  signature_copy_path?: string;
};

export const EMPTY_CFF_ITEM: CustomerFeedbackItemRow = {
  item_code: '',
  quantity: '',
  uom: '',
};

export const EMPTY_CUSTOMER_FEEDBACK_FORM: CustomerFeedbackFormData = {
  date: '',
  request_type: '',
  company_code: '',
  company_name: '',
  address: '',
  postal_code: '',
  territory_code: '',
  contact_person: '',
  contact_number: '',
  picker: '',
  packer: '',
  driver: '',
  items: Array.from({ length: 5 }, () => ({ ...EMPTY_CFF_ITEM })),
  reason: '',
  internal_remarks: '',
  invoice_no: '',
  invoice_date: '',
  credit_notes: '',
  submitted_by: '',
  invoice_issued_by: '',
  warehouse_action: '',
  warehouse_action_others: '',
  warehouse_reason: '',
  verified_by: '',
  approved_by: '',
  ack_date: '',
};

/** Certificate type checkboxes on PU-040 Certificate Request Form. */
export type CertificateRequestTypeKey =
  | 'food_grade_sgs'
  | 'msds'
  | 'sfa'
  | 'iso_22000'
  | 'others';

export type CertificateRequestItemRow = {
  item_no: string;
  vendor_no: string;
  material: string;
  remarks: string;
};

/** Admin Certificate Request — matches PU-040 Excel fields. */
export type CertificateRequestAttachmentRef = {
  path: string;
  fileName: string;
  size?: number;
  mime?: string;
};

export type CertificateRequestFormData = {
  date: string;
  customer_no: string;
  customer_name: string;
  sales_code: string;
  contact_person: string;
  contact_no: string;
  email: string;
  types: Record<CertificateRequestTypeKey, boolean>;
  type_others: string;
  items: CertificateRequestItemRow[];
  requested_by: string;
  prepared_by: string;
  verified_by: string;
  /** Customer request evidence (screenshots / emails / any files) — optional for now. */
  customer_requirement_attachments: CertificateRequestAttachmentRef[];
  /** Optional 6-month item sales report for auto-matching item lines. */
  sales_report_attachments: CertificateRequestAttachmentRef[];
  posted_at?: string;
  posted_by?: string;
};

export const EMPTY_CERTIFICATE_REQUEST_ITEM: CertificateRequestItemRow = {
  item_no: '',
  vendor_no: '',
  material: '',
  remarks: '',
};

export const EMPTY_CERTIFICATE_REQUEST_TYPES: Record<CertificateRequestTypeKey, boolean> = {
  food_grade_sgs: false,
  msds: false,
  sfa: false,
  iso_22000: false,
  others: false,
};

export const EMPTY_CERTIFICATE_REQUEST_FORM: CertificateRequestFormData = {
  date: '',
  customer_no: '',
  customer_name: '',
  sales_code: '',
  contact_person: '',
  contact_no: '',
  email: '',
  types: { ...EMPTY_CERTIFICATE_REQUEST_TYPES },
  type_others: '',
  items: Array.from({ length: 12 }, () => ({ ...EMPTY_CERTIFICATE_REQUEST_ITEM })),
  requested_by: '',
  prepared_by: '',
  verified_by: '',
  customer_requirement_attachments: [],
  sales_report_attachments: [],
};

export type PricingUpdateAttachmentRef = {
  path: string;
  fileName: string;
  size?: number;
  mime?: string;
};

export type PricingUpdateItemRow = {
  id: string;
  item_no: string;
  item_description: string;
  sales_uom: string;
  sales_price: string;
  moq: string;
  effective_date: string;
  ending_date: string;
};

/** Pricing Update (PUF) — requestor + authorised + approver workflow. */
export type PricingUpdateFormData = {
  customer_no: string;
  customer_name: string;
  customer_price_group: string;
  reason_requestor: string;
  reason_decision: string;
  requestor_attachments: PricingUpdateAttachmentRef[];
  authorised_attachments: PricingUpdateAttachmentRef[];
  items: PricingUpdateItemRow[];
};

let pufItemSeq = 0;
export function createPricingUpdateItemRow(
  partial?: Partial<PricingUpdateItemRow>,
): PricingUpdateItemRow {
  pufItemSeq += 1;
  return {
    id: String(partial?.id ?? '').trim() || `puf-item-${Date.now()}-${pufItemSeq}`,
    item_no: String(partial?.item_no ?? ''),
    item_description: String(partial?.item_description ?? ''),
    sales_uom: String(partial?.sales_uom ?? ''),
    sales_price: String(partial?.sales_price ?? ''),
    moq: String(partial?.moq ?? ''),
    effective_date: String(partial?.effective_date ?? ''),
    ending_date: String(partial?.ending_date ?? ''),
  };
}

export const EMPTY_PRICING_UPDATE_FORM: PricingUpdateFormData = {
  customer_no: '',
  customer_name: '',
  customer_price_group: '',
  reason_requestor: '',
  reason_decision: '',
  requestor_attachments: [],
  authorised_attachments: [],
  items: [createPricingUpdateItemRow()],
};

/** Internal Memorandum fields — matches ADM/MEMO Word template. */
export type InternalMemoFormData = {
  ref_no: string;
  to: string;
  cc: string;
  from_line: string;
  date: string;
  subject: string;
  salutation: string;
  body: string;
  issued_by: string;
  acknowledged_by: string;
  /** Set when memo is posted — uploaded signature copy. */
  signature_copy_name?: string;
  signature_copy_path?: string;
  posted_at?: string;
  posted_by?: string;
  cancelled_at?: string;
  cancelled_by?: string;
};

export const EMPTY_INTERNAL_MEMO_FORM: InternalMemoFormData = {
  ref_no: '',
  to: '',
  cc: '',
  from_line: '',
  date: '',
  subject: '',
  salutation: '',
  body: '',
  issued_by: '',
  acknowledged_by: '',
};

export type CitTransferStatus = 'New' | 'Processing' | 'Completed' | 'Cancelled' | '';

export type CitAttachmentRef = {
  path: string;
  fileName: string;
};

export type CitItemRow = {
  id: string;
  item_no: string;
  /** Manual transfer-to item code for stock transfer form. */
  item_no_transfer: string;
  description: string;
  sales_confirmation_qty: string;
  uom: string;
};

export type CitLinkedReceipt = {
  id: string;
  number: string;
  file?: CitAttachmentRef;
};

/** One item line on a deposit invoice (qty only; no warehouse split). */
export type CitDepositLine = {
  id: string;
  item_no: string;
  quantity: string;
};

export type CitDocumentRow = {
  id: string;
  number: string;
  invoice_amount: string;
  file?: CitAttachmentRef;
  /** Payment receipts and/or sales credit memos linked to this deposit. */
  payment_receipts: CitLinkedReceipt[];
  lines: CitDepositLine[];
};

/** One item line on an offset invoice (qty + warehouse split). */
export type CitOffsetLine = {
  id: string;
  item_no: string;
  quantity: string;
  warehouse_defu_qty: string;
  warehouse_depot_qty: string;
};

/** Offset invoice + linked stock transfer / warehouse split (multi-item). */
export type CitOffsetRow = {
  id: string;
  number: string;
  /** Whole-invoice amount (not per item). */
  invoice_amount: string;
  file?: CitAttachmentRef;
  stock_transfer_no: string;
  /** Prompted when creating the stock transfer form. */
  transfer_reason: string;
  /** Generated PDF stored in cit-attachments. */
  stock_transfer_form?: CitAttachmentRef;
  lines: CitOffsetLine[];
};

/**
 * Tooling invoice (mock-up / colour block charge, etc. — typically $0).
 * Records invoice no. (+ optional file) without deposit/offset qty.
 * JSON key remains `clearing_invoices` for stored submissions.
 */
export type CitClearingInvoiceRow = {
  id: string;
  number: string;
  file?: CitAttachmentRef;
};

/** Customise Items Transfer — Purchasing capture for later stock transfer export. */
export type CustomiseItemsTransferFormData = {
  customer_code: string;
  customer_name: string;
  status: CitTransferStatus;
  sales_confirmation_no: string;
  sales_confirmation_file?: CitAttachmentRef;
  /**
   * Manual Yes/No after SC attachment is uploaded.
   * null = not chosen yet (deposit/offset hidden).
   */
  needs_deposit: boolean | null;
  /** How the SC header/lines were created. */
  import_source?: 'manual' | 'powerbi';
  /** Last PowerBI upsert time (ISO); omit when locked by SC attachment. */
  pbi_synced_at?: string | null;
  items: CitItemRow[];
  deposit_invoices: CitDocumentRow[];
  offset_invoices: CitOffsetRow[];
  /**
   * Tooling invoices — mock-up / block charge cleared by invoice
   * (no offset / stock transfer). Shown for Deposit arrangement Yes or No.
   */
  clearing_invoices: CitClearingInvoiceRow[];
};

export function createCitItemRow(partial?: Partial<CitItemRow>): CitItemRow {
  return {
    id: partial?.id ?? crypto.randomUUID(),
    item_no: partial?.item_no ?? '',
    item_no_transfer: partial?.item_no_transfer ?? '',
    description: partial?.description ?? '',
    sales_confirmation_qty: partial?.sales_confirmation_qty ?? '',
    uom: partial?.uom ?? '',
  };
}

export function createCitLinkedReceipt(partial?: Partial<CitLinkedReceipt>): CitLinkedReceipt {
  return {
    id: partial?.id ?? crypto.randomUUID(),
    number: partial?.number ?? '',
    file: partial?.file,
  };
}

export function createCitDepositLine(partial?: Partial<CitDepositLine>): CitDepositLine {
  return {
    id: partial?.id ?? crypto.randomUUID(),
    item_no: partial?.item_no ?? '',
    quantity: partial?.quantity ?? '',
  };
}

export function createCitDocumentRow(partial?: Partial<CitDocumentRow>): CitDocumentRow {
  const receipts =
    partial?.payment_receipts && partial.payment_receipts.length > 0
      ? partial.payment_receipts.map((row) => createCitLinkedReceipt(row))
      : [createCitLinkedReceipt()];
  const lines =
    partial?.lines && partial.lines.length > 0
      ? partial.lines.map((line) => createCitDepositLine(line))
      : [createCitDepositLine()];
  return {
    id: partial?.id ?? crypto.randomUUID(),
    number: partial?.number ?? '',
    invoice_amount: partial?.invoice_amount ?? '',
    file: partial?.file,
    payment_receipts: receipts,
    lines,
  };
}

export function createCitOffsetLine(partial?: Partial<CitOffsetLine>): CitOffsetLine {
  return {
    id: partial?.id ?? crypto.randomUUID(),
    item_no: partial?.item_no ?? '',
    quantity: partial?.quantity ?? '',
    warehouse_defu_qty: partial?.warehouse_defu_qty ?? '',
    warehouse_depot_qty: partial?.warehouse_depot_qty ?? '',
  };
}

export function createCitOffsetRow(partial?: Partial<CitOffsetRow>): CitOffsetRow {
  const lines =
    partial?.lines && partial.lines.length > 0
      ? partial.lines.map((line) => createCitOffsetLine(line))
      : [createCitOffsetLine()];
  return {
    id: partial?.id ?? crypto.randomUUID(),
    number: partial?.number ?? '',
    invoice_amount: partial?.invoice_amount ?? '',
    file: partial?.file,
    stock_transfer_no: partial?.stock_transfer_no ?? '',
    transfer_reason: partial?.transfer_reason ?? '',
    stock_transfer_form: partial?.stock_transfer_form,
    lines,
  };
}

export function createCitClearingInvoiceRow(
  partial?: Partial<CitClearingInvoiceRow>,
): CitClearingInvoiceRow {
  return {
    id: partial?.id ?? crypto.randomUUID(),
    number: partial?.number ?? '',
    file: partial?.file,
  };
}

export function createEmptyCitForm(): CustomiseItemsTransferFormData {
  return {
    customer_code: '',
    customer_name: '',
    status: 'New',
    sales_confirmation_no: '',
    needs_deposit: null,
    import_source: 'manual',
    pbi_synced_at: null,
    items: [createCitItemRow()],
    // Deposit block appears only after SC upload + needs_deposit = Yes.
    deposit_invoices: [],
    offset_invoices: [],
    clearing_invoices: [],
  };
}

/** Prefer `createEmptyCitForm()` for new drafts so row ids stay unique. */
export const EMPTY_CIT_FORM: CustomiseItemsTransferFormData = createEmptyCitForm();

export const CIT_STATUS_OPTIONS: CitTransferStatus[] = [
  'New',
  'Processing',
  'Completed',
  'Cancelled',
];

export type ApplicationSubmissionSource = 'public' | 'internal';

export type ApplyForOption = 'new_customer' | 'new_outlet';

export type BillingSoaOption = 'individual' | 'group';

export type PaymentTermsOption =
  | 'cod'
  | '30_days'
  | '45_days'
  | '60_days'
  | 'paynow'
  | 'cod_1'
  | 'cod_3';

export type AccountCreationAttachmentRef = {
  path: string;
  name: string;
};

/** Customer-facing fields aligned with AR-004 REV 9 worksheet. */
export type AccountCreationFormData = {
  apply_for: ApplyForOption;
  billing_soa: BillingSoaOption;
  company_reg_no: string;
  gst_reg_no: string;
  billing_street: string;
  billing_building: string;
  billing_unit: string;
  billing_postal: string;
  outlet_name: string;
  delivery_street: string;
  delivery_building: string;
  delivery_unit: string;
  delivery_postal: string;
  finance_contact_name: string;
  finance_contact_no: string;
  finance_email: string;
  sales_contact_name: string;
  sales_contact_no: string;
  sales_email: string;
  /** Set on internal submissions before submit; public leaves empty. */
  salesperson_code: string;
  declaration_accepted: boolean;
  signatory_name: string;
  signatory_title: string;
  signatory_date: string;
  /** PNG data URL from signature pad (public applications). */
  signature_data_url: string;
  /** Legacy single ACRA fields — kept for older rows; prefer acra_attachments. */
  acra_attachment_path: string;
  acra_attachment_name: string;
  acra_attachments: AccountCreationAttachmentRef[];
  supporting_attachments: AccountCreationAttachmentRef[];
};

/** Filled by Finance on approval (AR-004 official use + delivery). */
export type AccountCreationFinanceData = {
  mother_account_code: string;
  outlet_code: string;
  businessgroup_code: string;
  division_code: string;
  salesperson_code: string;
  customer_price_group: string;
  payment_terms: PaymentTermsOption | '';
  remarks: string;
  delivery_schedule: string;
  cut_off_time: string;
  session: string;
  territory_code: string;
};

export const DEFAULT_ACCOUNT_CREATION_FINANCE: AccountCreationFinanceData = {
  mother_account_code: '',
  outlet_code: '',
  businessgroup_code: '',
  division_code: '10',
  salesperson_code: '',
  customer_price_group: '',
  payment_terms: '',
  remarks: '',
  delivery_schedule: '',
  cut_off_time: '12:00 PM',
  session: 'AM',
  territory_code: '',
};

/** Shared row shape used by UI; form_slug is filled by libs (not a DB column). */
export type ApplicationSubmission = {
  id: string;
  form_slug: ApplicationFormSlug;
  application_no: string;
  status: ApplicationSubmissionStatus;
  source: ApplicationSubmissionSource;
  company_name: string;
  form_data:
    | AccountCreationFormData
    | CustomerFeedbackFormData
    | InternalMemoFormData
    | CustomiseItemsTransferFormData
    | CertificateRequestFormData
    | PricingUpdateFormData;
  finance_data?: AccountCreationFinanceData;
  submitted_by: string | null;
  submitter_email: string | null;
  submitted_at: string;
  reviewed_by?: string | null;
  reviewed_by_username?: string | null;
  reviewed_at?: string | null;
  review_notes?: string | null;
  customer_code?: string | null;
  /** Account Creation → NAV CustomerCard push status */
  nav_sync_status?: 'pending' | 'success' | 'failed' | null;
  nav_synced_at?: string | null;
  nav_sync_error?: string | null;
  /** Pricing Update (PUF) workflow fields */
  authorised_by?: string | null;
  authorised_at?: string | null;
  approved_by?: string | null;
  approved_at?: string | null;
  rejected_by?: string | null;
  rejected_at?: string | null;
  reject_reason?: string | null;
  sharepoint_synced_at: string | null;
  sharepoint_sync_error: string | null;
  created_at: string;
  updated_at: string;
};

export type ApplicationFormDefinition = {
  slug: ApplicationFormSlug;
  title: string;
  department: string;
  description: string | null;
  public_enabled: boolean;
  sort_order: number;
  workflow?: FormWorkflowConfig;
};

export type FormWorkflowConfig = {
  submission: {
    public: boolean;
    internal: boolean;
    department_slugs: string[];
  };
  approval: {
    department_slugs: string[];
  };
  notifications: {
    on_submit_department_slugs: string[];
    on_approve_department_slugs: string[];
  };
};

export type ApplicationFormDepartment = {
  id: string;
  slug: string;
  name: string;
  sort_order: number;
  active: boolean;
};

export const EMPTY_ACCOUNT_CREATION_FORM: AccountCreationFormData = {
  apply_for: 'new_customer',
  billing_soa: 'individual',
  company_reg_no: '',
  gst_reg_no: '',
  billing_street: '',
  billing_building: '',
  billing_unit: '',
  billing_postal: '',
  outlet_name: '',
  delivery_street: '',
  delivery_building: '',
  delivery_unit: '',
  delivery_postal: '',
  finance_contact_name: '',
  finance_contact_no: '',
  finance_email: '',
  sales_contact_name: '',
  sales_contact_no: '',
  sales_email: '',
  salesperson_code: '',
  declaration_accepted: false,
  signatory_name: '',
  signatory_title: '',
  signatory_date: '',
  signature_data_url: '',
  acra_attachment_path: '',
  acra_attachment_name: '',
  acra_attachments: [],
  supporting_attachments: [],
};

/** Public customer form — standalone domain (no org Microsoft login). */
export function publicAccountCreationUrl(salespersonCode?: string): string {
  const fromEnv = (import.meta.env.VITE_PUBLIC_APPLY_URL ?? '').trim();
  const base = (fromEnv ? fromEnv.replace(/\/$/, '') : 'https://apply.banteckhan.com.sg/newacccreation');
  const code = (salespersonCode ?? '').trim();
  if (!code) return base;
  const url = new URL(base.includes('://') ? base : `https://${base}`);
  url.searchParams.set('sp', code);
  return url.toString();
}
